import { useCallback, useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Camera, Loader2, X, Image as ImageIcon, Trash2 } from "lucide-react";

export default function PhotoAttachments({ reportId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    if (!reportId) return setLoading(false);
    setLoading(true);
    try {
      const { data } = await api.get(`/reports/${reportId}/attachments`);
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [reportId]);

  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !reportId) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/reports/${reportId}/attachments`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(`${files.length} photo${files.length === 1 ? "" : "s"} attached`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this photo?")) return;
    try {
      await api.delete(`/attachments/${id}`);
      setItems((arr) => arr.filter((a) => a.id !== id));
      toast.success("Removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove");
    }
  };

  if (!reportId) {
    return (
      <div className="card-surface p-4 text-center text-sm text-clinical-subtext" data-testid="photos-empty">
        <ImageIcon className="h-5 w-5 mx-auto mb-1" />
        Submit the shift report first, then attach photos.
      </div>
    );
  }

  return (
    <div className="card-surface p-4" data-testid="photo-attachments">
      <div className="flex items-center justify-between mb-3">
        <span className="label-eyebrow flex items-center gap-2">
          <Camera className="h-3.5 w-3.5" /> Photos
        </span>
        <label
          className="inline-flex items-center gap-1.5 rounded-lg bg-clinical-primary text-white text-xs font-medium px-2.5 py-1.5 hover:bg-clinical-primaryHover cursor-pointer"
          data-testid="photo-upload-label"
        >
          {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Camera className="h-3 w-3" />}
          Attach
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
            multiple
            capture="environment"
            onChange={upload}
            className="hidden"
            data-testid="photo-upload-input"
          />
        </label>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-4 w-4 animate-spin text-clinical-subtext" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-sm text-clinical-subtext text-center py-4">
          No photos attached.
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {items.map((a) => {
            const url = `${API_BASE}/attachments/${a.id}/download`;
            return (
              <div
                key={a.id}
                className="relative group rounded-lg overflow-hidden border border-clinical-border bg-clinical-bg aspect-square"
                data-testid={`photo-${a.id}`}
              >
                <img
                  src={url}
                  alt={a.original_filename}
                  className="w-full h-full object-cover cursor-pointer"
                  onClick={() => setPreview(url)}
                />
                <button
                  onClick={() => remove(a.id)}
                  className="absolute top-1 right-1 rounded-md bg-black/60 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`photo-delete-${a.id}`}
                  aria-label="Remove"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {preview && (
        <div
          className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4"
          onClick={() => setPreview(null)}
        >
          <button className="absolute top-4 right-4 text-white" onClick={() => setPreview(null)}>
            <X className="h-6 w-6" />
          </button>
          <img src={preview} alt="Preview" className="max-w-full max-h-full rounded-xl" />
        </div>
      )}
    </div>
  );
}
